import { safeExternalHttpsUrl } from '../../domain/externalUrlPolicy';
import { aiChapterDisplayLabel } from '../../domain/aiChapterTargetPolicy';
import { escapeHtml } from './formatUtils';
import { formatTime, STATUS_LABEL, type AiJobDetail } from './aiJobDetailController';

/**
 * Renders an AI Create submission into the shared reader-dialog information
 * architecture (same .community-detail classes as the Community proposal
 * dialog): title/subtitle header, status row with a View Topic action, then
 * the submission brief sections. Differences from the proposal renderer are
 * content-level only: a Learning Preferences section replaces voting, and
 * the status pill mirrors the job lifecycle.
 */
const linkIcon =
	'<svg aria-hidden="true" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><path d="M10 13a5 5 0 0 0 7.54.54l3-3a5 5 0 0 0-7.07-7.07l-1.72 1.71"></path><path d="M14 11a5 5 0 0 0-7.54-.54l-3 3a5 5 0 0 0 7.07 7.07l1.71-1.71"></path></svg>';
const attachmentIcon =
	'<svg aria-hidden="true" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><path d="M21.44 11.05l-9.19 9.19a6 6 0 0 1-8.49-8.49l9.19-9.19a4 4 0 0 1 5.66 5.66l-9.2 9.19a2 2 0 0 1-2.83-2.83l8.49-8.48"></path></svg>';

function detailSection(label: string, className: string, content: string): string {
	return `<section class="community-detail-section ${className}"><h3>${label}</h3>${content || '<p class="community-detail-empty">—</p>'}</section>`;
}

function humanFileSize(size: number): string {
	if (!Number.isFinite(size) || size <= 0) return '0 B';

	const units = ['B', 'KB', 'MB', 'GB'];
	let value = size;
	let index = 0;
	while (value >= 1024 && index < units.length - 1) {
		value /= 1024;
		index++;
	}
	return `${value >= 10 || index === 0 ? Math.round(value) : value.toFixed(1)} ${units[index]}`;
}

export function renderAiSubmissionDetail(job: AiJobDetail): string {
	const status = STATUS_LABEL[job.status] ?? { text: 'In Progress', cls: 'pill-progress' };
	const topicSlug = job.topicSlug?.trim() ?? '';
	const topic = topicSlug
		? `<button type="button" class="btn btn-outline btn-sm community-detail-topic" data-proto-click="showAiSubmissionTopic('${escapeHtml(topicSlug)}')">View Topic</button>`
		: '';
	const preferences: string[] = [];
	if ((job.description ?? job.prompt ?? '').trim()) preferences.push(`<p>${escapeHtml((job.description ?? job.prompt ?? '').trim())}</p>`);
	preferences.push(`<p class="ai-submission-detail-chapters">Requested chapters: ${escapeHtml(aiChapterDisplayLabel(job.requestedChaptersRange ?? job.requestedChapters) || 'AI decides')}</p>`);
	const strengthen = (job.strengthenItems ?? [])
		.filter(Boolean)
		.map((value) => `<li>${escapeHtml(value)}</li>`)
		.join('');
	const weaken = (job.weakenItems ?? [])
		.filter(Boolean)
		.map((value) => `<li>${escapeHtml(value)}</li>`)
		.join('');
	const links = (job.links ?? [])
		.map((value) => {
			const href = safeExternalHttpsUrl(value.url);
			return href
				? `<a class="community-detail-link" href="${escapeHtml(href)}" target="_blank" rel="noopener noreferrer">${linkIcon}<span>${escapeHtml(value.label?.trim() || href)}</span></a>`
				: '';
		})
		.join('');
	const attachments = (job.attachments ?? [])
		.map(
			(value) =>
				`<div class="community-detail-attachment">${attachmentIcon}<span class="community-detail-attachment-name">${escapeHtml(value.name)}</span><span class="community-detail-attachment-meta">${humanFileSize(value.size ?? 0)} · ${escapeHtml(value.mimeType ?? 'file')}</span></div>`,
		)
		.join('');

	return `<article class="community-detail" data-job-id="${escapeHtml(job.id)}" data-status="${escapeHtml(job.status)}" data-has-topic="${String(Boolean(topicSlug))}"><h2 class="community-detail-title">${escapeHtml(job.title)}</h2>${job.subtitle?.trim() ? `<p class="community-detail-subtitle">${escapeHtml(job.subtitle)}</p>` : ''}<div class="community-detail-status-row"><span class="pill ${status.cls}">${status.text}</span><span class="ai-submission-detail-time">${escapeHtml(formatTime(job.queuedAt, job.completedAt))}</span>${topic}</div>${detailSection('Learning Preferences', 'community-detail-preferences', preferences.join(''))}${detailSection('Strengthen', 'community-detail-strengthen', strengthen ? `<ul>${strengthen}</ul>` : '')}${detailSection('Weaken', 'community-detail-weaken', weaken ? `<ul>${weaken}</ul>` : '')}${detailSection('Reference Links', 'community-detail-links', links)}${detailSection('Attachments', 'community-detail-attachments', attachments)}</article>`;
}
